import type { OpportunityMapDomains, OpportunityMapItem, OpportunityStatus } from '@fig/shared';

import { formatCurrency, formatDate, formatScore } from '@/lib/formatters';
import { formatOpportunityStatus } from '@/lib/opportunity-status';

export const MAP_EXTENT = 8;
export const MAP_HALF_EXTENT = MAP_EXTENT / 2;
export const MAP_DISC_RADIUS = 4.35;
export const MAP_MIN_Y = -3.2;
export const MAP_MAX_Y = 3.2;
export const NODE_MIN_RADIUS = 0.055;
export const NODE_MAX_RADIUS = 0.22;
const DAYS_PER_YEAR = 365.25;

export interface OpportunityMapPoint {
  item: OpportunityMapItem;
  position: [number, number, number];
  radius: number;
  color: string;
  dimmed: boolean;
}

export interface TooltipRow {
  label: string;
  value: string;
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function getDomain(values: number[]): { min: number; max: number } {
  if (values.length === 0) return { min: 0, max: 0 };
  return values.reduce(
    (domain, value) => ({
      min: Math.min(domain.min, value),
      max: Math.max(domain.max, value),
    }),
    { min: values[0]!, max: values[0]! }
  );
}

function normalizeLinear(
  value: number | null | undefined,
  domain: { min: number; max: number },
  min: number,
  max: number
): number {
  if (!isFiniteNumber(value) || !Number.isFinite(domain.min) || !Number.isFinite(domain.max)) {
    return (min + max) / 2;
  }

  if (domain.max === domain.min) {
    return (min + max) / 2;
  }

  const pct = (value - domain.min) / (domain.max - domain.min);
  return min + clamp(pct, 0, 1) * (max - min);
}

function normalizeLog(
  value: number | null | undefined,
  domain: { min: number; max: number },
  min: number,
  max: number
): number {
  if (!isFiniteNumber(value) || value <= 0 || domain.max <= 0) {
    return (min + max) / 2;
  }

  const domainMin = Math.max(domain.min, 1);
  const domainMax = Math.max(domain.max, 1);

  if (domainMax === domainMin) {
    return (min + max) / 2;
  }

  const logMin = Math.log10(domainMin);
  const logMax = Math.log10(domainMax);
  const logValue = Math.log10(Math.max(value, 1));
  const pct = (logValue - logMin) / (logMax - logMin);

  return min + clamp(pct, 0, 1) * (max - min);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function getOpportunityNodeRadius(
  estimatedValue: number | null,
  domains: OpportunityMapDomains
): number {
  const normalized = normalizeLog(estimatedValue, domains.estimatedValue, 0, 1);
  return NODE_MIN_RADIUS + normalized * (NODE_MAX_RADIUS - NODE_MIN_RADIUS);
}

export function getOpportunityNodeColor(score: number, _status: OpportunityStatus): string {
  return score > 75 ? '#a7b0bd' : '#ff8a1f';
}

export function getYearsUntilSurrender(
  surrenderDate: string | null | undefined,
  now = new Date()
): number | null {
  if (!surrenderDate) return null;

  const parsed = new Date(surrenderDate);
  if (Number.isNaN(parsed.getTime())) return null;

  const years = (parsed.getTime() - now.getTime()) / (DAYS_PER_YEAR * 24 * 60 * 60 * 1000);
  return Number.isFinite(years) ? years : null;
}

export function buildOpportunityMapPoints(
  items: OpportunityMapItem[],
  domains: OpportunityMapDomains
): OpportunityMapPoint[] {
  const surrenderDomain = getDomain(
    items.map((item) => getYearsUntilSurrender(item.surrenderDate)).filter(isFiniteNumber)
  );
  const rawPoints = items.map((item) => ({
    item,
    rawPosition: [
      normalizeLog(item.accountValue, domains.accountValue, -MAP_HALF_EXTENT, MAP_HALF_EXTENT),
      normalizeLinear(item.propensityScore, domains.propensityScore, MAP_MIN_Y, MAP_MAX_Y),
      normalizeLinear(
        getYearsUntilSurrender(item.surrenderDate),
        surrenderDomain,
        -MAP_HALF_EXTENT,
        MAP_HALF_EXTENT
      ),
    ] as [number, number, number],
    radius: getOpportunityNodeRadius(item.estimatedValue, domains),
    color: getOpportunityNodeColor(item.propensityScore, item.status),
    dimmed:
      item.status === 'dismissed' || item.status === 'actioned' || item.status === 'withdrawn',
  }));

  if (rawPoints.length === 0) return [];

  const centroid = rawPoints.reduce<[number, number, number]>(
    (acc, point) => [
      acc[0] + point.rawPosition[0],
      acc[1] + point.rawPosition[1],
      acc[2] + point.rawPosition[2],
    ],
    [0, 0, 0]
  );
  centroid[0] /= rawPoints.length;
  centroid[1] /= rawPoints.length;
  centroid[2] /= rawPoints.length;

  const centered = rawPoints.map((point) => {
    const position: [number, number, number] = [
      point.rawPosition[0] - centroid[0],
      point.rawPosition[1] - centroid[1],
      point.rawPosition[2] - centroid[2],
    ];

    return { ...point, position };
  });

  const maxHorizontalDistance = Math.max(
    1,
    ...centered.map((point) => Math.hypot(point.position[0], point.position[2]))
  );
  const maxVerticalDistance = Math.max(1, ...centered.map((point) => Math.abs(point.position[1])));

  return centered.map(({ rawPosition: _rawPosition, ...point }) => ({
    ...point,
    position: [
      (point.position[0] / maxHorizontalDistance) * MAP_DISC_RADIUS,
      (point.position[1] / maxVerticalDistance) * MAP_MAX_Y,
      (point.position[2] / maxHorizontalDistance) * MAP_DISC_RADIUS,
    ],
  }));
}

export function getOpportunityTooltipRows(item: OpportunityMapItem): TooltipRow[] {
  return [
    { label: 'Score', value: formatScore(item.propensityScore) },
    { label: 'Account Value', value: formatCurrency(item.accountValue) },
    { label: 'Est. Commission', value: formatCurrency(item.estimatedValue) },
    { label: 'Owner', value: formatOwner(item) },
    { label: 'Jurisdiction', value: item.jurisdiction ?? '-' },
    { label: 'Surrender', value: formatDate(item.surrenderDate) },
    { label: 'Sales Team', value: item.salesTeam ?? '-' },
    { label: 'Advisor', value: item.advisorId || '-' },
    { label: 'Status', value: formatOpportunityStatus(item.status) },
  ];
}

function formatOwner(item: OpportunityMapItem): string {
  if (item.ownerName && item.ownerAge) return `${item.ownerName}, ${item.ownerAge}`;
  if (item.ownerName) return item.ownerName;
  if (item.ownerAge) return `Age ${item.ownerAge}`;
  return '-';
}
